import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { API_BASE } from "../../../ApiUrl";
import "./AddDomain.css";
import SettingsTabs from "../Settings/SettingsTabs";

const emptyDomain = {
  domain: "",
};

const AddDomain = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([{ ...emptyDomain }]);

  const [tenantId, setTenantId] = useState(null);

  const [bulkFile, setBulkFile] = useState(null);
  const [bulkDomains, setBulkDomains] = useState([]);

  const fileInputRef = useRef(null);

  // ----------------------------------------------------------
  // GET TENANT ID
  // ----------------------------------------------------------

  useEffect(() => {
    const storedTenantUser = localStorage.getItem("tenant_user");

    if (!storedTenantUser) {
      Swal.fire({
        icon: "error",
        title: "Tenant information missing",
        text: "Please login again.",
      });
      return;
    }

    try {
      const tenantUser = JSON.parse(storedTenantUser);

      if (!tenantUser.tenant_id) {
        Swal.fire({
          icon: "error",
          title: "Tenant ID missing",
          text: "Tenant information could not be found.",
        });
        return;
      }

      setTenantId(Number(tenantUser.tenant_id));
    } catch (error) {
      console.error("Invalid tenant_user:", error);

      Swal.fire({
        icon: "error",
        title: "Invalid tenant session",
        text: "Please login again.",
      });
    }
  }, []);

  // ----------------------------------------------------------
  // FETCH DOMAINS
  // ----------------------------------------------------------

  useEffect(() => {
    if (tenantId) {
      fetchDomains();
    }
  }, [tenantId]);

const fetchDomains = async () => {
  try {
    setLoading(true);

    const response = await axios.post(`${API_BASE}/tenant-records/`, {
      tenant_id: Number(tenantId),
      tables: ["tenant_domains"],
    });

    console.log("Tenant records response:", response.data);

    let data = response.data;

    if (response.data?.data) {
      data = response.data.data;
    }

    // Handle possible table-wise response
    if (data?.tenant_domains) {
      data = data.tenant_domains;
    }

    if (!Array.isArray(data)) {
      data = [];
    }

    setDomains(data);
  } catch (error) {
    console.error("Error fetching tenant domains:", error);

    Swal.fire({
      icon: "error",
      title: "Unable to load domains",
      text:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Something went wrong while loading domains.",
    });
  } finally {
    setLoading(false);
  }
};

  // ----------------------------------------------------------
  // HANDLE DOMAIN CHANGE
  // ----------------------------------------------------------

  const handleChange = (index, value) => {
    setFormData((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              domain: value,
            }
          : item
      )
    );
  };

  // ----------------------------------------------------------
  // ADD DOMAIN FORM
  // ----------------------------------------------------------

  const handleAdd = () => {
    setFormData([{ ...emptyDomain }]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  // ----------------------------------------------------------
  // ADD ANOTHER DOMAIN
  // ----------------------------------------------------------

  const handleAddAnother = () => {
    setFormData((prev) => [
      ...prev,
      {
        ...emptyDomain,
      },
    ]);
  };

  // ----------------------------------------------------------
  // REMOVE DOMAIN FORM
  // ----------------------------------------------------------

  const handleRemoveForm = (index) => {
    if (formData.length === 1) {
      return;
    }

    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  // ----------------------------------------------------------
  // EDIT DOMAIN
  // ----------------------------------------------------------

  const handleEdit = (domain) => {
    setFormData([
      {
        domain: domain.domain || "",
      },
    ]);

    setEditingId(domain.id);
    setIsEditing(true);
    setShowForm(true);
  };

  // ----------------------------------------------------------
  // DELETE DOMAIN
  // ----------------------------------------------------------

  const handleDelete = async (domain) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Domain?",
      text: `Are you sure you want to delete domain "${
        domain.domain || "this domain"
      }"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);

      await axios.delete(`${API_BASE}/tenant-domains/${domain.id}/`);

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Domain deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchDomains();
    } catch (error) {
      console.error("Error deleting domain:", error);

      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Unable to delete the domain.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // SUBMIT DOMAINS
  // ----------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant.",
      });

      return;
    }

    const cleanedDomains = formData.map((item) =>
      item.domain.trim().toLowerCase()
    );

    const emptyDomainIndex = cleanedDomains.findIndex(
      (domain) => !domain
    );

    if (emptyDomainIndex !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Domain Required",
        text: `Please enter domain for Domain ${emptyDomainIndex + 1}.`,
      });

      return;
    }

    const duplicateDomains = cleanedDomains.filter(
      (domain, index) =>
        cleanedDomains.indexOf(domain) !== index
    );

    if (duplicateDomains.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Domain",
        text: `The domain "${duplicateDomains[0]}" has been entered more than once.`,
      });

      return;
    }

    try {
      setLoading(true);

      // ------------------------------------------------------
      // UPDATE
      // ------------------------------------------------------

      if (isEditing) {
        const payload = {
          domain: cleanedDomains[0],
          tenant: Number(tenantId),
        };

        await axios.put(
          `${API_BASE}/tenant-domains/${editingId}/`,
          payload
        );

        await Swal.fire({
          icon: "success",
          title: "Domain Updated",
          text: "Domain updated successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      // ------------------------------------------------------
      // CREATE MULTIPLE DOMAINS
      // ------------------------------------------------------

      else {
        for (const domain of cleanedDomains) {
          await axios.post(`${API_BASE}/tenant-domains/`, {
            domain,
            tenant: Number(tenantId),
          });
        }

        await Swal.fire({
          icon: "success",
          title: "Domains Created",
          text: `${cleanedDomains.length} domain${
            cleanedDomains.length > 1 ? "s" : ""
          } added successfully.`,
          timer: 1800,
          showConfirmButton: false,
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyDomain }]);

      await fetchDomains();
    } catch (error) {
      console.error("Error saving domains:", error);

      const backendError =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data ||
        "Unable to save domain.";

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text:
          typeof backendError === "string"
            ? backendError
            : JSON.stringify(backendError),
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // CANCEL
  // ----------------------------------------------------------

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyDomain }]);
    setBulkFile(null);
    setBulkDomains([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };








  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="domain-page">
          <SettingsTabs />

          {/* ==================================================
              DOMAIN LIST
          ================================================== */}

          {!showForm && (
            <>
              <div className="domain-page-header">
                <div>
                  <h2>Email Domains</h2>
                </div>

                <div className="domain-header-actions">
                  

                  <button
                    type="button"
                    className="domain-add-btn"
                    onClick={handleAdd}
                  >
                    + Add Domain
                  </button>
                </div>
              </div>

              <div className="domain-list-card">
                {loading ? (
                  <div className="domain-loading">
                    Loading domains...
                  </div>
                ) : domains.length === 0 ? (
                  <div className="domain-empty">
                    <div className="domain-empty-icon">@</div>

                    <h3>No Domains Found</h3>

                    <p>
                      Add the email domains used by your organisation.
                    </p>

                    <div className="domain-empty-actions">
            

                      <button
                        type="button"
                        className="domain-empty-add-btn"
                        onClick={handleAdd}
                      >
                        + Add Domain
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="domain-table-wrapper">
                    <table className="domain-table">
                      <thead>
                        <tr>
                          <th>Email Domain</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {domains.map((domain) => (
                          <tr key={domain.id}>
                            <td>
                              <div className="domain-name-cell">
                                <div className="domain-avatar">@</div>

                                <div>
                                  <strong>
                                    {domain.domain || "-"}
                                  </strong>

                                  <span>Email Domain</span>
                                </div>
                              </div>
                            </td>

                            <td>
                              <div className="domain-actions">
                                <button
                                  type="button"
                                  className="domain-edit-btn"
                                  onClick={() => handleEdit(domain)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="domain-delete-btn"
                                  onClick={() => handleDelete(domain)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ==================================================
              ADD / EDIT DOMAIN FORM
          ================================================== */}

          {showForm && (
            <>
              <div className="domain-page-header">
                <div>
                  <h2>
                    {isEditing ? "Edit Domain" : "Add Domains"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="domain-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="domain-card">
                  <div className="domain-form-body">
                    {formData.map((item, index) => (
                      <div
                        className="domain-repeat-form"
                        key={index}
                      >
                        <div className="domain-repeat-header">
                          <div className="domain-section-title">
                            Domain {index + 1}
                          </div>

                          {!isEditing && formData.length > 1 && (
                            <button
                              type="button"
                              className="domain-remove-btn"
                              onClick={() =>
                                handleRemoveForm(index)
                              }
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="domain-form-grid">
                          <div className="domain-form-group">
                            <label>
                              Email Domain <span>*</span>
                            </label>

                            <div className="domain-input-wrapper">
                              <span className="domain-prefix">
                                @
                              </span>

                              <input
                                type="text"
                                value={item.domain}
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    e.target.value
                                  )
                                }
                                placeholder="acmecorp.com"
                                maxLength={255}
                                required
                              />
                            </div>

                            <small className="domain-field-hint">
                              Enter the company email domain
                              without @.
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}

                    {!isEditing && (
                      <button
                        type="button"
                        className="domain-add-another-btn"
                        onClick={handleAddAnother}
                      >
                        + Add Another Domain
                      </button>
                    )}

                   
                  </div>
                </div>

                <div className="domain-form-actions">
                  <button
                    type="button"
                    className="domain-cancel-btn"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="domain-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Domain"
                      : "Save Domains"}
                  </button>
                </div>
              </form>
            </>
          )}

       
        </div>
      </div>
    </div>
  );
};

export default AddDomain;